const ADMIN_SECRET = import.meta.env.VITE_ADMIN_PATH_SECRET ?? '__ops_dev';
const ADMIN_API_BASE = `/api/v1/${ADMIN_SECRET}`;

export class AdminApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export type AdminFetchOptions = RequestInit & { skipAuth?: boolean };

function getCurrentLanguage(): string {
  return localStorage.getItem('i18nextLng') || 'pt-BR';
}

/**
 * Admin-plane HTTP client. Mirrors apiFetch but:
 *  - reads token from `admin_jwt` (NEVER `jwt`)
 *  - on 401: clears `admin_jwt` + dispatches `admin:unauthorized`
 *  - NEVER touches `localStorage.jwt` or dispatches `auth:unauthorized`
 */
export async function adminApiFetch<T>(path: string, options: AdminFetchOptions = {}): Promise<T> {
  const { skipAuth, ...rest } = options;
  const token = skipAuth ? null : localStorage.getItem('admin_jwt');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept-Language': getCurrentLanguage(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...rest.headers,
  };

  const response = await fetch(`${ADMIN_API_BASE}${path}`, { ...rest, headers });

  if (response.status === 401 && !skipAuth) {
    localStorage.removeItem('admin_jwt');
    window.dispatchEvent(new Event('admin:unauthorized'));
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}) as Record<string, unknown>);
    const err = (data as { error?: { message?: string; code?: string } }).error;
    throw new AdminApiError(response.status, err?.message ?? response.statusText, err?.code);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}
